export const SKINS = ['#f2c6a0', '#dba277', '#a96b47', '#70412f', '#442c25'];
export const OUTFITS = ['#d84937', '#3055a0', '#ed8840', '#e5bd40', '#8255a8', '#202b38'];
export const HAIR = ['Short', 'Curls', 'Bob', 'Bun'];
export type Character = {
  name: string;
  skin: number;
  outfit: number;
  hair: number;
  body?: 'male' | 'female';
  look?: 'casual' | 'mandem';
  // exact colours and build from the character creator; when missing, the palettes above are used
  skinColour?: string;
  outfitColour?: string;
  hairColour?: string;
  buildScale?: number;
  origin: 'fresh' | 'comfortable';
  trait: 'Social' | 'Ambitious' | 'Creative';
};
export type Needs = { hunger: number; energy: number; hygiene: number; fun: number; social: number };
export type Game = {
  character: Character;
  money: number;
  debt: number;
  minute: number;
  needs: Needs;
  job: string | null;
  level: number;
  skill: number;
  shifts: number;
  home: number;
  location: string;
  x: number;
  z: number;
  visited: string[];
  friends: string[];
  log: string[];
  version: number;
};
export type Action = { type: string; [key: string]: unknown };
export const LOCATIONS = [
  {
    id: 'home',
    name: 'Your place',
    area: 'North London',
    x: -19,
    z: -11,
    icon: 'Home',
    tag: 'Rest & recharge',
    description: 'A room in a London house share. The kitchen has a rota. Nobody follows it.',
  },
  {
    id: 'cafe',
    name: 'The Daily Grind',
    area: 'Bloomsbury',
    x: -5,
    z: -11,
    icon: 'Coffee',
    tag: 'Coffee & conversation',
    description: 'Good coffee, a proper breakfast and a barista who remembers your order.',
  },
  {
    id: 'office',
    name: 'City offices',
    area: 'The City',
    x: 10,
    z: -12,
    icon: 'Briefcase',
    tag: 'Work & build skills',
    description: 'Find your first role, finish a shift and work towards your next promotion.',
  },
  {
    id: 'market',
    name: 'Camden market',
    area: 'Camden',
    x: 22,
    z: -9,
    icon: 'ShoppingBag',
    tag: 'Food & side gigs',
    description: 'Browse the stalls, grab something to eat or pick up a market shift.',
  },
  {
    id: 'pub',
    name: 'The Fox & Finch',
    area: 'Soho',
    x: -21,
    z: 1,
    icon: 'Beer',
    tag: 'Find your people',
    description: 'A cosy local with a quiz night, questionable darts and strong opinions about football.',
  },
  {
    id: 'park',
    name: 'Green park',
    area: 'Westminster',
    x: -7,
    z: 0,
    icon: 'Trees',
    tag: 'A little breathing room',
    description: 'Take a walk, read on a bench and forget about your inbox for a bit.',
  },
  {
    id: 'tube',
    name: 'Underground',
    area: 'Central London',
    x: 8,
    z: 0,
    icon: 'TrainFront',
    tag: 'Across the city',
    description: 'Mind the gap. Choose a destination on your phone to explore the neighbourhood.',
  },
  {
    id: 'gym',
    name: 'Neighbourhood gym',
    area: 'South Bank',
    x: 22,
    z: 1,
    icon: 'Dumbbell',
    tag: 'Build a routine',
    description: 'A workout, a shower and a small victory before the next busy day.',
  },
  {
    id: 'bigben',
    name: 'Westminster',
    area: 'By the Thames',
    x: -16,
    z: 16,
    icon: 'Landmark',
    tag: 'A London moment',
    description: 'Watch the river, take a photo and remember why you wanted to live here.',
  },
  {
    id: 'eye',
    name: 'London Eye',
    area: 'South Bank',
    x: 15,
    z: 17,
    icon: 'FerrisWheel',
    tag: 'See the city differently',
    description: 'Catch up with a friend on the South Bank or take a ride above the skyline.',
  },
] as const;
export const JOBS = [
  {
    id: 'barista',
    title: 'Barista',
    place: 'The Daily Grind',
    pay: 85,
    skill: 'Hospitality',
    hours: 5,
    at: 'cafe',
    description: 'Early starts. Flat whites. Regulars with stories.',
  },
  {
    id: 'developer',
    title: 'Junior developer',
    place: 'City offices',
    pay: 140,
    skill: 'Technology',
    hours: 7,
    at: 'office',
    description: 'Ship something useful. Survive the stand-up.',
  },
  {
    id: 'retail',
    title: 'Retail assistant',
    place: 'Camden market',
    pay: 95,
    skill: 'Customer service',
    hours: 6,
    at: 'market',
    description: 'A busy shop floor and a proper team.',
  },
  {
    id: 'creative',
    title: 'Content creator',
    place: 'South Bank',
    pay: 75,
    skill: 'Creativity',
    hours: 4,
    at: 'eye',
    description: 'Find your voice and turn ideas into a career.',
  },
] as const;
export const HOMES = [
  {
    name: 'House share',
    rent: 120,
    cost: 0,
    description: 'A room, a shared kitchen and a mysterious missing teaspoon.',
  },
  { name: 'Studio flat', rent: 220, cost: 1200, description: 'Your own front door. Your own washing-up.' },
  { name: 'One-bed flat', rent: 340, cost: 3000, description: 'Room to stretch out, with a little London view.' },
];
export const PEOPLE = [
  {
    id: 'alex',
    name: 'Alex',
    place: 'pub',
    line: 'The quiz starts at eight. I know nothing about geography, but I am excellent at ordering chips.',
  },
  { id: 'maya', name: 'Maya', place: 'cafe', line: 'I moved here for a year. That was six years ago. Fancy a coffee?' },
  {
    id: 'sam',
    name: 'Sam',
    place: 'park',
    line: 'Best thing about this city? Finding a quiet bench when you need one.',
  },
  {
    id: 'ellie',
    name: 'Ellie',
    place: 'market',
    line: 'The first rule of Camden: eat before you start looking at all the food stalls.',
  },
];
export function newGame(character: Character): Game {
  return {
    character,
    money: character.origin === 'comfortable' ? 1800 : 450,
    debt: character.origin === 'comfortable' ? 0 : 300,
    minute: 480,
    needs: { hunger: 82, energy: 95, hygiene: 90, fun: 65, social: 58 },
    job: null,
    level: 1,
    skill: 0,
    shifts: 0,
    home: 0,
    location: 'park',
    x: -7,
    z: 0,
    visited: ['park'],
    friends: [],
    log: ['A new chapter. A little room. A whole city to make your own.'],
    version: 0,
  };
}
const clamp = (n: number) => Math.max(0, Math.min(100, n));
function pay(g: Game, n: number) {
  if (g.money < n) throw new Error(`You need £${n}. Try a shift or a side gig first.`);
  g.money -= n;
}
function time(g: Game, hours: number) {
  const oldWeek = Math.floor(g.minute / (7 * 1440));
  g.minute += Math.round(hours * 60);
  for (const k of Object.keys(g.needs) as (keyof Needs)[])
    g.needs[k] = clamp(g.needs[k] - hours * { hunger: 5, energy: 3, hygiene: 2, fun: 1.4, social: 1.6 }[k]);
  if (Math.floor(g.minute / (7 * 1440)) > oldWeek) {
    const rent = HOMES[g.home].rent,
      paid = Math.min(g.money, rent);
    g.money -= paid;
    g.debt += rent - paid;
    g.log.unshift(
      `Weekly rent: £${rent}${paid < rent ? '. Unpaid rent was added to your debt.' : '. Paid. One less thing to worry about.'}`,
    );
  }
}
function boost(g: Game, v: Partial<Needs>) {
  for (const [k, n] of Object.entries(v)) g.needs[k as keyof Needs] = clamp(g.needs[k as keyof Needs] + (n ?? 0));
}
export function performAction(source: Game, a: Action): { game: Game; message: string } {
  const g = structuredClone(source);
  let message = '';
  switch (a.type) {
    case 'travel': {
      const l = LOCATIONS.find((v) => v.id === a.location);
      if (!l) throw new Error('Choose a place on the map.');
      time(g, 0.25);
      g.location = l.id;
      g.x = l.x;
      g.z = l.z;
      if (!g.visited.includes(l.id)) g.visited.push(l.id);
      message = `You arrived at ${l.name}.`;
      break;
    }
    case 'job': {
      const j = JOBS.find((v) => v.id === a.job);
      if (!j) throw new Error('Choose a career.');
      if (g.job === j.id) throw new Error('You already have this job.');
      g.job = j.id;
      g.level = 1;
      g.skill = 0;
      message = `Hired! Your first ${j.title.toLowerCase()} shift pays £${j.pay}.`;
      break;
    }
    case 'work': {
      const j = JOBS.find((v) => v.id === g.job);
      if (!j) throw new Error('Choose a job first.');
      if (g.needs.energy < 25 || g.needs.hunger < 20) throw new Error('Have a meal and rest before your shift.');
      const l = LOCATIONS.find((v) => v.id === j.at)!;
      g.location = l.id;
      g.x = l.x;
      g.z = l.z;
      if (!g.visited.includes(l.id)) g.visited.push(l.id);
      time(g, j.hours);
      const wage = j.pay + (g.level - 1) * 45;
      g.money += wage;
      g.shifts++;
      g.skill += g.character.trait === 'Ambitious' ? 25 : 20;
      message = `Shift done. £${wage} earned.`;
      if (g.skill >= 100 && g.level < 5) {
        g.level++;
        g.skill -= 100;
        message += ` Promotion! You reached level ${g.level}.`;
      }
      boost(g, { social: 8 });
      break;
    }
    case 'train':
      if (!g.job) throw new Error('Choose a career first.');
      time(g, 2);
      g.skill += 15;
      boost(g, { fun: 5 });
      if (g.skill >= 100 && g.level < 5) {
        g.level++;
        g.skill -= 100;
        message = 'Your practice paid off. Promotion!';
      } else message = 'Two hours of practice. Career skill +15.';
      break;
    case 'meal':
      pay(g, 8);
      time(g, 0.5);
      boost(g, { hunger: 55, fun: 4 });
      message = 'A proper meal. You are ready to face London again.';
      break;
    case 'coffee':
      pay(g, 4);
      time(g, 0.25);
      boost(g, { energy: 18, social: 6 });
      message = 'One flat white, a little people-watching. Lovely.';
      break;
    case 'sleep':
      g.location = 'home';
      g.x = -19;
      g.z = -11;
      time(g, 8);
      boost(g, { energy: 100, fun: 8 });
      message = 'Eight hours of sleep. Your housemate even kept the noise down.';
      break;
    case 'shower':
      time(g, 0.5);
      boost(g, { hygiene: 90 });
      message = 'Fresh clothes. Fresh start.';
      break;
    case 'walk':
      time(g, 1);
      boost(g, { fun: 25, energy: 8 });
      message = 'A walk through the park. Even the weather behaved.';
      break;
    case 'quiz':
      pay(g, 6);
      time(g, 2);
      boost(g, { fun: 40, social: 35 });
      message = 'Your team came third. Nobody agreed on the music round.';
      break;
    case 'workout':
      pay(g, 7);
      time(g, 1);
      boost(g, { fun: 18, hygiene: -15, energy: -10 });
      message = 'Workout complete. A shower would be a good next move.';
      break;
    case 'photo':
      time(g, 0.5);
      boost(g, { fun: g.character.trait === 'Creative' ? 28 : 18 });
      message = 'A London photo worth keeping. The pigeon got in the shot, obviously.';
      break;
    case 'ride':
      pay(g, 20);
      time(g, 1);
      boost(g, { fun: 40, social: 10 });
      message = 'Above the rooftops, London looks a little less complicated.';
      break;
    case 'gig':
      if (g.needs.energy < 15) throw new Error('Rest before taking another gig.');
      time(g, 2);
      g.money += 45;
      boost(g, { social: 10 });
      message = 'You helped on a market stall. £45 earned and a few new faces.';
      break;
    case 'chat': {
      const p = PEOPLE.find((v) => v.id === a.person);
      if (!p) throw new Error('Choose someone to talk to.');
      time(g, 0.5);
      boost(g, { social: g.character.trait === 'Social' ? 35 : 25, fun: 10 });
      if (!g.friends.includes(p.id)) g.friends.push(p.id);
      message = `${p.name}: ${p.line}`;
      break;
    }
    case 'home': {
      const n = Number(a.home);
      if (!Number.isInteger(n) || n < 0 || n >= HOMES.length) throw new Error('Choose a home.');
      if (n === g.home) throw new Error('You already live here.');
      pay(g, HOMES[n].cost);
      g.home = n;
      message = `Keys collected! Your new ${HOMES[n].name.toLowerCase()} costs £${HOMES[n].rent} a week.`;
      break;
    }
    case 'debt': {
      if (g.debt === 0) throw new Error('You have no debt to repay.');
      const p = Math.min(g.debt, g.money, 100);
      if (p <= 0) throw new Error('Earn a little money first.');
      g.money -= p;
      g.debt -= p;
      message = `£${p} repaid. £${g.debt} remaining.`;
      break;
    }
    case 'character': {
      const c = validateCharacter(a.character);
      g.character = { ...c, origin: g.character.origin };
      message = 'Your new look is saved.';
      break;
    }
    case 'wait':
      time(g, 1);
      message = 'An hour in London passes.';
      break;
    default:
      throw new Error('That action is not available.');
  }
  g.version++;
  g.log.unshift(message);
  g.log = g.log.slice(0, 20);
  return { game: g, message };
}
export function validateCharacter(raw: unknown): Character {
  const c = raw as Character;
  if (!c || typeof c.name !== 'string' || c.name.trim().length < 2 || c.name.trim().length > 24)
    throw new Error('Use a name between 2 and 24 characters.');
  if (
    !Number.isInteger(c.skin) ||
    c.skin < 0 ||
    c.skin >= SKINS.length ||
    !Number.isInteger(c.outfit) ||
    c.outfit < 0 ||
    c.outfit >= OUTFITS.length ||
    !Number.isInteger(c.hair) ||
    c.hair < 0 ||
    c.hair >= HAIR.length ||
    (c.body !== undefined && !['male', 'female'].includes(c.body)) ||
    (c.look !== undefined && !['casual', 'mandem'].includes(c.look)) ||
    (c.body === 'female' && c.look === 'mandem') ||
    !['fresh', 'comfortable'].includes(c.origin) ||
    !['Social', 'Ambitious', 'Creative'].includes(c.trait)
  )
    throw new Error('Choose your character options.');
  return { ...c, name: c.name.trim(), body: c.body ?? 'male', look: c.look ?? 'casual' };
}
export function clock(m: number) {
  return `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
export function day(m: number) {
  return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][Math.floor(m / 1440) % 7];
}
